import React, { useEffect } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  styled,
} from "@mui/material"
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormTextField from "components/form/FormTextField";
import StyledButton from "components/button/StyledButton";
import "../../components/button/StyledButton.css"
import { useNavigate } from 'react-router-dom';
import { useFormContext } from '../../context/FormContext';
import { useCreateEcomMutation } from 'api/api';
import { Ecom } from '../../types/types';
import { getErrorMessage } from 'lib/utils/getErrorMessage';

interface AddEcomFormProps {
  title: string;
  stage: number;
  inputLabels: Array<{
    name: string;
    label: string;
    unit: string;
    value?: number | string | null;
    type: string;
    component: string;
    latestValue?: number | string | null;
  }>;
  validationSchema: z.ZodType<any, any, any>;
}

interface ECOMData {
  material_category: string;
  material_type: string;
  a1_a3_carbon_factor: number;
  a1_a3_carbon_factor_unit: string;
  version: number;
}

const AddEcomForm: React.FC<AddEcomFormProps> = ({
  title,
  stage,
  inputLabels,
  validationSchema,
}) => {
  const { ecomFormData, setEcomFormData } = useFormContext();
  const [createEcom, { error: createError, isLoading }] = useCreateEcomMutation();

  const navigate = useNavigate();
  // If the number of stages is increased, increase this value to reflect that
  const maxStage = 6;

  const generateDefaultValues = (inputLabels) =>
    inputLabels.reduce((acc, { name, value }) => {
      acc[name] = ecomFormData[name] !== undefined ? ecomFormData[name] : value || "";
      return acc;
    }, {});

  const form = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: generateDefaultValues(inputLabels)
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const handleClickCancel = () => {
    // clear form
    form.reset(generateDefaultValues(inputLabels));
    setEcomFormData({});
    // return to ecom page
    navigate('/ecom');
  }

  const handleSubmitForm = async (data: any) => {
    // This is a fix to resolve an issue with Zod data validation
    // When a user types a value into the input box, it is being read as a string
    // Convert all values to strings before saving to the context
    const stringData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, String(value)])
    );
    setEcomFormData((prevData) => ({ ...prevData, ...stringData }));

    // Check if this is the final step
    if (stage < maxStage) {
      console.log("GO TO NEXT PAGE");
      // Navigate to the next page if not the final step
      navigate(`/add-ecom/${stage + 1}`);
    } else {
      // Perform final submission
      try {
        const materialMapping = {
          "2,500 PSI": { category: "Concrete", unit: "kg CO2e/CY", material_type: "2500 PSI" },
          "3,000 PSI": { category: "Concrete", unit: "kg CO2e/CY", material_type: "3000 PSI" },
          "4,000 PSI": { category: "Concrete", unit: "kg CO2e/CY", material_type: "4000 PSI" },
          "5,000 PSI": { category: "Concrete", unit: "kg CO2e/CY", material_type: "5000 PSI" },
          "6,000 PSI": { category: "Concrete", unit: "kg CO2e/CY", material_type: "6000 PSI" },
          "8,000 PSI": { category: "Concrete", unit: "kg CO2e/CY", material_type: "8000 PSI" },
          "3,000 PSI LW": { category: "Concrete", unit: "kg CO2e/CY", material_type: "3000 PSI LW" },
          "4,000 PSI LW": { category: "Concrete", unit: "kg CO2e/CY", material_type: "4000 PSI LW" },
          "5,000 PSI LW": { category: "Concrete", unit: "kg CO2e/CY", material_type: "5000 PSI LW" },
          "rebar": { category: "Steel Reinforcement", unit: "kg CO2e/ton", material_type: "Rebar" },
          "welded_wire_reinforcement": { category: "Steel Reinforcement", unit: "kg CO2e/ton", material_type: "Welded Wire Reinforcement" },
          "post_tensioning": { category: "Steel Reinforcement", unit: "kg CO2e/ton", material_type: "Post Tensioning" },
          "normal_weight_masonry_block": { category: "Masonry", unit: "kg CO2e/ton", material_type: "Normal Weight Masonry Block" },
          "light_weight_masonry_block": { category: "Masonry", unit: "kg CO2e/ton", material_type: "Light Weight Masonry Block" },
          "masonry_grout": { category: "Masonry", unit: "kg CO2e/CY", material_type: "Masonry Grout" },
          "mortar": { category: "Masonry", unit: "kg CO2e/CY", material_type: "Mortar" },
          "fabricated_HR_structural": { category: "Steel", unit: "kg CO2e/ton", material_type: "Rolled Steel Shapes" },
          "fabricated_structural_plate": { category: "Steel", unit: "kg CO2e/ton", material_type: "Plate Steel Fabrications" },
          "fabricated_hollow_structural": { category: "Steel", unit: "kg CO2e/ton", material_type: "Tube Steel (HSS)" },
          "open_web_steel_joists": { category: "Steel", unit: "kg CO2e/ton", material_type: "Open Web Steel Joists" },
          "steel_deck": { category: "Steel", unit: "kg CO2e/ton", material_type: "Steel Deck" },
          "cold_form_metal_framing": { category: "Steel", unit: "kg CO2e/ton", material_type: "Cold Formed Metal Framing" },
          "softwood_lumber": { category: "Timber", unit: "kg CO2e/CF", material_type: "Softwood Lumber" },
          "softwood_plywood": { category: "Timber", unit: "kg CO2e/CF", material_type: "Softwood Plywood" },
          "glued_laminated_timber": { category: "Timber", unit: "kg CO2e/CF", material_type: "Glue Laminated Timber" },
          "cross_laminated_timber": { category: "Timber", unit: "kg CO2e/CF", material_type: "Cross Laminated Timber" },
          "laminated_veneer_lumber": { category: "Timber", unit: "kg CO2e/CF", material_type: "Laminated Veneer Lumber" },
          "wood_i_joists": { category: "Timber", unit: "kg CO2e/pound", material_type: "Wood I Joists" },
        };
        // Build the ECOMData array
        const ECOMData = Object.entries(ecomFormData).reduce((acc: ECOMData[], [key, value]) => {
          if (materialMapping[key]) {
            const { category, unit, material_type } = materialMapping[key];
            acc.push({
              material_category: category,
              material_type: material_type,
              a1_a3_carbon_factor: Number(value),
              a1_a3_carbon_factor_unit: unit,
              version: ecomFormData.versionNumber,
            });
          }
          return acc;
        }, []);
        // Create the final object for submission
        const finalFormData = {
          ECOMData,
          version: ecomFormData.versionNumber,
          description: ecomFormData.descriptionOfUpdate,
        };
        // console.log('finalFormData = ', finalFormData);
        const response = await createEcom(finalFormData as Ecom).unwrap();
        // console.log('response = ', response);
        // clear form
        form.reset(generateDefaultValues(inputLabels));
        setEcomFormData({});
        // return to Ecom page with modal
        navigate('/ecom?modal=open&modalType=addEcomSuccess');
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    }
  };

  // Will not reach a negative number since the back button is disabled on the first stage
  const handleClickBack = () => {
    navigate(`/add-ecom/${stage - 1}`);
  }

  useEffect(() => {
    const subscription = watch((values) => {
      setEcomFormData((prevData) => ({ ...prevData, ...values }));
    });
    return () => subscription.unsubscribe();
  }, [watch, setEcomFormData]);

  return (
    <StyledContainer>
      <div style={{ width: '100%', height: '100%', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 41, paddingTop: 37 }}>
        <Box sx={{ width: "100%" }}>
          <div style={{ width: '100%', height: '100%', color: '#1A1A1A', fontSize: 28, fontFamily: 'Helvetica Neue LT Std', fontWeight: '300', wordWrap: 'break-word', paddingBottom: 12 }}>{title}</div>
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            {title != 'Description' ? <Box sx={{ display: 'flex', width: '100%' }}>
              <div style={{ width: '20%', color: '#555555', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontWeight: '700', wordWrap: 'break-word' }}>Material Type</div>
              <div style={{ width: '45%', color: '#555555', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontWeight: '700', wordWrap: 'break-word' }}>A1-A3 Carbon Factor </div>
            </Box> : null}
            {inputLabels.map((inputLabel) => (
              <Controller
                key={inputLabel.name}
                name={inputLabel.name}
                control={control}
                render={({ field }) => {
                  const errorMessage = errors[inputLabel.name]?.message as string | undefined
                  return (
                    <Box>
                      {
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                        }}>
                          {/* Material Type Label */}
                          <div style={{ width: '20%', height: '100%', color: '#555555', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontWeight: '700', wordWrap: 'break-word' }}>
                            {inputLabel.label}
                          </div>
                          <div style={{
                            width: '10%',
                          }}>
                            <FormTextField
                              {...field}
                              label={inputLabel.unit}
                              error={!!errors[inputLabel.name]}
                              helperText={errorMessage}
                              disabled={isSubmitting || isLoading}
                            />
                          </div>
                          <div style={{
                            width: '20%',
                            marginLeft: 10,
                            alignItems: 'center'
                          }}>
                            <span style={{ color: '#555555', fontSize: 12, fontFamily: 'Helvetica Neue LT Std', fontWeight: '700' }}>
                              {inputLabel.label !== "Description Of Update" ? "(Active version value: " + inputLabel.latestValue + " " + inputLabel.unit + ")" : null}
                            </span>
                          </div>
                        </div>
                        // )  
                      }
                    </Box>
                  )
                }}
              />
            ))}
            <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-start", mb: 1 }}>
              <StyledButton label="Cancel" type="outlined" onClick={handleClickCancel} />
              {stage > 1 && <StyledButton label="Back" type="outlined" onClick={handleClickBack} />}
              <StyledButton
                label={stage === maxStage ? "Submit" : "Next"}
                type="filled"
                onClick={handleSubmit(handleSubmitForm)}
                disabled={isLoading}
                endAdornment={isLoading && (
                  <CircularProgress
                    size={16}
                    sx={{ color: '#fff' }}
                  />
                )}
              />
            </Box>
            {createError && (
              <Alert severity="error">
                {getErrorMessage(createError)}
              </Alert>
            )}
          </form>
        </Box>
      </div >
    </StyledContainer >
  );
}

const StyledContainer = styled(Container)({
  marginBottom: 24,
});

export default AddEcomForm;